import React, { useRef, useState } from "react";
import {
  BackspaceRounded,
  CheckCircleRounded,
  Close,
  WhatsApp,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  GlobalStyles,
  IconButton,
  Typography,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { useRazorpay } from "react-razorpay";
import PrimaryButton from "../Reusables/PrimaryButton";
import { API, RAZOR_PAY } from "../../utils/Api";
import { toMoneyNumber } from "../../utils/tax";
import {
  PAYMENT_STATUS,
  clearPendingPayment,
  dispatchPaymentCheck,
  finalizeQuickOrder,
  patchPendingPayment,
  savePendingPayment,
} from "../../utils/pendingPayment";

const keypadNumbers = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

const vibrateKeypad = (pattern = 12) => {
  if (
    typeof navigator === "undefined" ||
    typeof navigator.vibrate !== "function"
  ) {
    return;
  }

  try {
    navigator.vibrate(pattern);
  } catch {
    // Vibration is unsupported or blocked on this device.
  }
};

const formatReadyTime = (minutesToAdd = 0) => {
  const readyAt = new Date();
  readyAt.setMinutes(readyAt.getMinutes() + Number(minutesToAdd || 0));

  return readyAt.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const mapPickupOption = (orderType) => {
  if (orderType === "TAKE-AWAY" || orderType === "TAKE-AWAY") {
    return "TAKE-AWAY";
  }

  return "DINE-IN";
};

const getMakingMinutes = (cart = []) => {
  const makingMinutes = cart.reduce((maxMinutes, cartItem) => {
    const itemMinutes = Number(
      cartItem?.makingTime || cartItem?.preparationTime || 0,
    );

    return itemMinutes > maxMinutes ? itemMinutes : maxMinutes;
  }, 0);

  return makingMinutes > 0 ? makingMinutes : 20;
};

const getMenuGroupId = (outletData, cart = []) =>
  outletData?.menuGroup?._id ||
  outletData?.menuGroup ||
  outletData?.menuGroupId ||
  cart
    .map(
      (cartItem) =>
        cartItem?.menuGroup?._id ||
        cartItem?.menuGroup ||
        cartItem?.menuGroupId,
    )
    .find(Boolean) ||
  null;

const normalizePhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");

  if (digits.length >= 10) {
    return digits.slice(-10);
  }

  return "";
};

const getOutletPhoneFromRecord = (outlet) =>
  normalizePhone(
    outlet?.phone ||
      outlet?.phoneNumber ||
      outlet?.mobile ||
      outlet?.mobileNumber ||
      outlet?.contactNumber ||
      outlet?.contact ||
      outlet?.franchisePhone,
  );

const fetchOutletPhoneNumber = async (outletId, outletData) => {
  const fromOutletData = getOutletPhoneFromRecord(outletData);

  if (fromOutletData) {
    return fromOutletData;
  }

  if (!outletId) {
    return "";
  }

  try {
    const response = await axios.get(
      `${API.BASE_URL}outlet/getOutletCityPincode`,
    );
    const records = [
      ...(response?.data?.data?.totalOutletRecords || []),
      ...(response?.data?.data?.comingSoonOutlets || []),
    ];
    const matchedOutlet = records.find((outlet) => outlet?._id === outletId);

    return getOutletPhoneFromRecord(matchedOutlet);
  } catch (error) {
    console.error(
      "Error fetching outlet phone:",
      error?.response?.data || error,
    );
    return "";
  }
};

const extractRazorpayOrder = (payload) => {
  const nested =
    payload?.order ||
    payload?.razorpayOrder ||
    payload?.razorpay_order ||
    payload;

  const orderId =
    nested?.id ||
    nested?.order_id ||
    nested?.razorpayOrderId ||
    payload?.razorpayOrderId ||
    payload?.orderId ||
    null;

  return {
    orderId,
    amount: nested?.amount ?? payload?.amount ?? null,
    currency: nested?.currency || payload?.currency || "INR",
  };
};

const getAmountInPaise = (amount, grandTotal) => {
  const numericAmount = Number(amount);
  const fallback = Math.round(toMoneyNumber(grandTotal) * 100);

  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return fallback;
  }

  if (numericAmount < toMoneyNumber(grandTotal) * 10) {
    return Math.round(numericAmount * 100);
  }

  return Math.round(numericAmount);
};

const PaymentProcessing = ({
  embedded = false,
  cart = [],
  outletData = null,
  orderType,
  paymentMode = "ONLINE",
  cartProductRawMaterials = [],
  subtotal = 0,
  taxAmount = 0,
  discountAmount = 0,
  grandTotal = 0,
  isCreatingOrder = false,
  setIsCreatingOrder,
  onClose,
  onOrderCreated,
  onOrderError,
}) => {
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [isProceeding, setIsProceeding] = useState(false);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [shake, setShake] = useState(false);
  const { Razorpay } = useRazorpay();
  const orderContextRef = useRef({
    payload: null,
    phone: "",
    prefillPhone: "",
  });
  const checkoutLeftAppRef = useRef(false);
  const checkoutVisibilityListenerRef = useRef(null);

  const stopCheckoutVisibilityTracking = () => {
    if (!checkoutVisibilityListenerRef.current) return;
    document.removeEventListener(
      "visibilitychange",
      checkoutVisibilityListenerRef.current,
    );
    checkoutVisibilityListenerRef.current = null;
  };

  const startCheckoutVisibilityTracking = () => {
    stopCheckoutVisibilityTracking();
    checkoutLeftAppRef.current = document.visibilityState === "hidden";

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        checkoutLeftAppRef.current = true;
        patchPendingPayment({ leftApp: true });
      }
    };

    checkoutVisibilityListenerRef.current = onVisibilityChange;
    document.addEventListener("visibilitychange", onVisibilityChange);
  };

  const isLoading = isProceeding || isCreatingOrder || isVerifyingPayment;

  const handleNumberClick = (number) => {
    if (isLoading || phone.length >= 10) return;

    const nextLength = phone.length + 1;

    setPhone((prev) => `${prev}${number}`);
    setError("");
    vibrateKeypad(nextLength === 10 ? [14, 40, 22] : 12);
  };

  const handleBackspace = () => {
    if (isLoading || !phone.length) return;
    setPhone((prev) => prev.slice(0, -1));
    setError("");
    vibrateKeypad(8);
  };

  const handleClear = () => {
    if (isLoading) return;
    setPhone("");
    setError("");
  };

  const validatePhone = () => {
    let phoneError = "";

    if (!phone) {
      phoneError = "Mobile number is required.";
    } else if (!/^\d{10}$/.test(phone)) {
      phoneError = "Enter a valid 10-digit mobile number.";
    }

    setError(phoneError);

    if (phoneError) {
      setShake(true);
      setTimeout(() => setShake(false), 450);
      return false;
    }

    return true;
  };

  const buildRazorpayInitiatePayload = () => {
    const makingMinutes = getMakingMinutes(cart);
    const arrivalTimeDurationInMinutes =
      makingMinutes + Number(API.BUFFER_TIME || 0);
    const menuGroup = getMenuGroupId(outletData, cart);
    const pickupOption = mapPickupOption(orderType);

    const customerCartItemsData = cart.map((cartItem) => {
      const quantity = Number(cartItem?.quantity || 1);
      const productPrice = toMoneyNumber(cartItem?.productPrice || 0);
      const addonPrice = toMoneyNumber(cartItem?.addonPrice || 0);
      const unitPrice = toMoneyNumber(
        cartItem?.unitPrice ?? productPrice + addonPrice,
      );
      const taxable = toMoneyNumber(cartItem?.taxable ?? unitPrice * quantity);
      const itemTaxAmount = toMoneyNumber(cartItem?.taxAmount || 0);
      const itemGrandTotal = toMoneyNumber(
        cartItem?.grandTotal ?? cartItem?.totalPrice ?? taxable + itemTaxAmount,
      );
      // Backend validates mrpRate against product.newMrp (product only, no addons).
      const mrpRate = toMoneyNumber(cartItem?.mrpRate ?? productPrice);
      // Exclusive product rate (without addon prices), matching sample payload shape.
      const rate = toMoneyNumber(
        quantity > 0 ? taxable / quantity - addonPrice : productPrice,
      );

      return {
        quantity,
        mrpRate,
        taxable,
        taxAmount: itemTaxAmount,
        grandTotal: itemGrandTotal,
        sourceType: "QUICK-ORDER",
        extraAddonsInfo: null,
        product: cartItem?.productId,
        addons: (Array.isArray(cartItem?.addons) ? cartItem.addons : []).map(
          (addon) => ({
            _id:
              addon?.addonAllocationId || addon?.addonId || addon?._id || null,
            selectedValue:
              addon?.selectedValue || addon?.label || addon?.name || "",
            price: Number(addon?.price || 0),
            selectedValueId:
              addon?.selectedValueId || addon?.valueId || addon?._id || null,
          }),
        ),
        rate,
      };
    });

    return {
      arrivalTimeDurationInMinutes,
      ordersType: "PICKUP",
      deliveryDetails: null,
      outlet: outletData?._id,
      paymentMode: "ONLINE",
      readyTime: formatReadyTime(arrivalTimeDurationInMinutes),
      pickupOption,
      makingTimeDuration: `${makingMinutes} minutes`,
      customerCartData: {
        totalTaxable: toMoneyNumber(subtotal || 0),
        totalTaxAmount: toMoneyNumber(taxAmount || 0),
        deliveryCharges: null,
        grandTotal: toMoneyNumber(grandTotal || 0),
        menuGroup,
        paymentInitiatedAt: new Date().toISOString(),
        ordersType: "PICKUP",
        source: "QUICK-ORDER",
        outlet: outletData?._id,
        cartProductRawMaterials: Array.isArray(cartProductRawMaterials)
          ? cartProductRawMaterials
          : [],
      },
      customerCartItemsData,
    };
  };

  const completePaidOrder = async (razorpayResponse) => {
    stopCheckoutVisibilityTracking();
    setIsVerifyingPayment(true);
    setIsProceeding(true);
    setIsCreatingOrder?.(true);
    setError("");

    const razorpayOrderId = razorpayResponse.razorpay_order_id;

    patchPendingPayment({
      razorpayOrderId,
      razorpayPaymentId: razorpayResponse.razorpay_payment_id,
      razorpaySignature: razorpayResponse.razorpay_signature,
      status: PAYMENT_STATUS.PAID,
    });

    try {
      const createdOrder = await finalizeQuickOrder({
        razorpayOrderId,
        razorpayPaymentId: razorpayResponse.razorpay_payment_id,
        razorpaySignature: razorpayResponse.razorpay_signature,
        payload: orderContextRef.current.payload || {},
        phone: orderContextRef.current.phone || "",
        pollAttempts: 8,
        pollDelayMs: 1500,
      });

      if (!createdOrder) {
        throw new Error("Unable to create order after payment.");
      }

      clearPendingPayment(razorpayOrderId);
      onOrderCreated?.(createdOrder, razorpayOrderId);
    } catch (apiError) {
      console.error(
        "Complete paid order error:",
        apiError?.response?.data || apiError,
      );

      const message =
        apiError?.response?.data?.message ||
        apiError?.response?.data?.error ||
        apiError?.message ||
        "Payment received, but we couldn't confirm the order. Please contact the cafe.";

      setError(message);
      onOrderError?.(message);
    } finally {
      setIsVerifyingPayment(false);
      setIsProceeding(false);
      setIsCreatingOrder?.(false);
    }
  };

  const openRazorpayCheckout = (initiatedPayload) => {
    const { orderId, amount, currency } = extractRazorpayOrder(initiatedPayload);

    if (!orderId) {
      const message = "Unable to start payment. Missing Razorpay order id.";
      setError(message);
      onOrderError?.(message);
      return;
    }

    if (!Razorpay) {
      const message = "Payment gateway is still loading. Please try again.";
      setError(message);
      onOrderError?.(message);
      return;
    }

    savePendingPayment({
      razorpayOrderId: orderId,
      outletId: outletData?._id,
      phone: orderContextRef.current.phone || "",
      payload: orderContextRef.current.payload || {},
      status: PAYMENT_STATUS.CHECKOUT_OPEN,
    });

    startCheckoutVisibilityTracking();

    const options = {
      key: RAZOR_PAY.RAZOR_PAY_KEY,
      order_id: orderId,
      amount: getAmountInPaise(amount, grandTotal),
      currency,
      name: "Xpanse",
      description: "Quick Order",
      theme: {
        hide_topbar: false,
        color: "#09A5F7",
        backdrop_color: "rgba(9, 165, 247, 0.45)",
      },
      prefill: {
        ...(orderContextRef.current.prefillPhone
          ? { contact: orderContextRef.current.prefillPhone }
          : {}),
      },
      readonly: {
        ...(orderContextRef.current.prefillPhone ? { contact: true } : {}),
      },
      modal: {
        ondismiss: () => {
          stopCheckoutVisibilityTracking();
          setIsProceeding(false);
          setIsCreatingOrder?.(false);
          dispatchPaymentCheck({
            cancelled: !checkoutLeftAppRef.current,
          });
        },
      },
      handler: (response) => {
        completePaidOrder(response);
      },
    };

    const rzpay = new Razorpay(options);

    rzpay.on("payment.failed", (failedResponse) => {
      console.error("Razorpay payment failed:", failedResponse?.error);
      stopCheckoutVisibilityTracking();
      const message =
        failedResponse?.error?.description ||
        "Payment failed. Please try again.";
      setError(message);
      onOrderError?.(message);
      setIsProceeding(false);
      setIsCreatingOrder?.(false);
      dispatchPaymentCheck({ cancelled: true });
    });

    rzpay.open();
  };

  const initiateRazorpayOrder = async ({ skipped = false } = {}) => {
    if (isLoading) return null;

    if (!outletData?._id) {
      const message = "Outlet information is missing.";
      setError(message);
      onOrderError?.(message);
      return null;
    }

    if (!orderType) {
      const message = "Please select an order type.";
      setError(message);
      onOrderError?.(message);
      return null;
    }

    if (!Array.isArray(cart) || cart.length === 0) {
      const message = "Cart is empty.";
      setError(message);
      onOrderError?.(message);
      return null;
    }

    setIsProceeding(true);
    setIsCreatingOrder?.(true);
    setError("");

    const payload = buildRazorpayInitiatePayload();
    orderContextRef.current.payload = payload;
    orderContextRef.current.phone = skipped ? "" : normalizePhone(phone);
    orderContextRef.current.prefillPhone = skipped
      ? await fetchOutletPhoneNumber(outletData?._id, outletData)
      : orderContextRef.current.phone;

    try {
      const response = await axios.post(
        `${API.BASE_URL}order/initiate-web-based-razorpay-order`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const responseData = response?.data?.data || response?.data || null;
      const { orderId } = extractRazorpayOrder(responseData);

      if (orderId) {
        savePendingPayment({
          razorpayOrderId: orderId,
          outletId: outletData?._id,
          phone: orderContextRef.current.phone || "",
          payload,
          status: PAYMENT_STATUS.CHECKOUT_OPEN,
        });
      }

      if (skipped) {
        sessionStorage.removeItem("userPhone");
        sessionStorage.setItem("phoneSkipped", "true");
      } else {
        sessionStorage.setItem("userPhone", phone);
        sessionStorage.removeItem("phoneSkipped");
      }

      return responseData;
    } catch (apiError) {
      console.error(
        "Initiate Razorpay order API error:",
        apiError?.response?.data || apiError,
      );

      const message =
        apiError?.response?.data?.message ||
        apiError?.response?.data?.error ||
        apiError?.message ||
        "Unable to initiate payment. Please try again.";

      setError(message);
      onOrderError?.(message);
      return null;
    } finally {
      setIsProceeding(false);
      setIsCreatingOrder?.(false);
    }
  };

  const startPayment = async ({ skipped = false } = {}) => {
    const initiatedOrder = await initiateRazorpayOrder({ skipped });

    if (!initiatedOrder) return;

    openRazorpayCheckout(initiatedOrder);
  };

  const handleProceed = async () => {
    if (!validatePhone()) return;
    await startPayment({ skipped: false });
  };

  const handleSkip = async () => {
    if (isLoading) return;

    setPhone("");
    setError("");
    await startPayment({ skipped: true });
  };

  return (
    <>
      {!embedded && (
        <GlobalStyles
          styles={{
            html: {
              width: "100%",
              minHeight: "100%",
              backgroundColor: "#F4FBFE",
            },
            body: {
              width: "100%",
              minHeight: "100%",
              margin: 0,
              backgroundColor: "#F4FBFE",
              overscrollBehavior: "none",
            },
            "#root": {
              width: "100%",
              minHeight: "100dvh",
            },
            "*": {
              boxSizing: "border-box",
            },
          }}
        />
      )}

      <Box
        sx={{
          width: "100%",
          minHeight: embedded ? "100%" : "100dvh",
          background:
            "linear-gradient(180deg, #DDEFF6 0px, #F4FBFE 118px, #F8FDFF 100%)",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "430px",
            minHeight: embedded ? "100%" : "100dvh",
            display: "flex",
            flexDirection: "column",
            pb: "max(16px, env(safe-area-inset-bottom))",
          }}
        >
          <MobileHeader
            onClose={onClose}
            closeDisabled={isCreatingOrder || isProceeding || isVerifyingPayment}
          />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              px: 2,
            }}
          >
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: "22px",
                backgroundColor: "rgba(255,255,255,0.92)",
                border: "1px solid #D5EAF3",
                boxShadow: "0 10px 30px rgba(23, 101, 135, 0.08)",
              }}
            >
              <Typography
                component="h1"
                sx={{
                  color: "#0B4A67",
                  fontSize: "26px",
                  fontWeight: 800,
                  lineHeight: 1.15,
                  letterSpacing: "-0.7px",
                }}
              >
                Enter mobile number
              </Typography>

              <Typography
                sx={{
                  mt: 0.8,
                  color: "#668594",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: 1.5,
                }}
              >
                We'll use it to send your order invoice and updates.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <motion.div
                  animate={shake ? { x: [-10, 10, -7, 7, -4, 4, 0] } : {}}
                  transition={{ duration: 0.4 }}
                >
                  <PhoneDisplay phone={phone} hasError={Boolean(error)} />
                </motion.div>

                <Box sx={{ minHeight: 22, mt: 0.75 }}>
                  <AnimatePresence mode="wait">
                    {error ? (
                      <motion.div
                        key="error"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                      >
                        <Typography
                          sx={{
                            color: "#D94848",
                            fontSize: "12px",
                            fontWeight: 700,
                          }}
                        >
                          {error}
                        </Typography>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="hint"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        <Typography
                          sx={{
                            color: "#8BA3AE",
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          {phone.length}/10 digits
                        </Typography>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "none" }}>
              <motion.div
                animate={shake ? { x: [-10, 10, -7, 7, -4, 4, 0] } : {}}
                transition={{ duration: 0.4 }}
              >
                <PhoneDisplay phone={phone} hasError={Boolean(error)} />
              </motion.div>

              <Box sx={{ minHeight: 24, mt: 0.75 }}>
                <AnimatePresence mode="wait">
                  {error ? (
                    <motion.div
                      key="error"
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                    >
                      <Typography
                        sx={{
                          color: "#D94848",
                          fontSize: "12px",
                          fontWeight: 700,
                        }}
                      >
                        {error}
                      </Typography>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="hint"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <Typography
                        sx={{
                          color: "#8BA3AE",
                          fontSize: "12px",
                          fontWeight: 600,
                        }}
                      >
                        {phone.length}/10 digits
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                mt: 2,
              }}
            >
              <Keypad
                phone={phone}
                isLoading={isLoading}
                handleNumberClick={handleNumberClick}
                handleBackspace={handleBackspace}
                handleClear={handleClear}
              />

              <Box sx={{ mt: 2.5 }}>
                <PrimaryButton
                  title="Continue"
                  onClick={handleProceed}
                  disabled={isLoading || phone.length !== 10}
                  spinner={isLoading}
                  disabledTitle={isLoading ? "Please wait" : undefined}
                  style={{
                    width: "100%",
                    minHeight: "54px",
                    border: "none",
                    borderRadius: "16px",
                    color: "#FFFFFF",
                    fontSize: "16px",
                    fontWeight: 800,
                    letterSpacing: "0.1px",
                    padding: "12px 0",
                    background:
                      phone.length === 10
                        ? "linear-gradient(180deg, #16B8EE 0%, #04A5E2 100%)"
                        : undefined,
                    boxShadow:
                      phone.length === 10
                        ? "0 8px 20px rgba(4, 165, 226, 0.24)"
                        : "none",
                  }}
                />

                <Typography
                  component="button"
                  type="button"
                  onClick={handleSkip}
                  disabled={isLoading}
                  sx={{
                    width: "100%",
                    mt: 1.4,
                    py: 1,
                    border: 0,
                    background: "transparent",
                    color: "#537789",
                    fontSize: "14px",
                    fontWeight: 700,
                    cursor: isLoading ? "default" : "pointer",
                    opacity: isLoading ? 0.45 : 1,
                    WebkitTapHighlightColor: "transparent",
                  }}
                >
                  Skip and continue
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {isVerifyingPayment && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            backgroundColor: "rgba(12, 42, 58, 0.72)",
            backdropFilter: "blur(4px)",
          }}
        >
          <CircularProgress sx={{ color: "#FFFFFF" }} />
          <Typography
            sx={{
              color: "#FFFFFF",
              fontSize: "16px",
              fontWeight: 700,
            }}
          >
            Confirming your payment...
          </Typography>
        </Box>
      )}
    </>
  );
};

const MobileHeader = ({ onClose, closeDisabled }) => (
  <Box
    sx={{
      position: "sticky",
      top: 0,
      zIndex: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      minHeight: 56,
      px: 1.25,
      py: 1,
      borderBottomLeftRadius: "18px",
      borderBottomRightRadius: "18px",
      backgroundColor: "#09A5F7",
    }}
  >
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.8,
        px: 1.15,
        py: 0.8,
        borderRadius: "14px",
        backgroundColor: "#FFFFFF",
        border: "1px solid #CFE7F0",
        boxShadow: "0 3px 10px rgba(20, 124, 168, 0.05)",
      }}
    >
      <WhatsApp sx={{ color: "#22B866", fontSize: 18 }} />
      <Typography
        sx={{
          color: "#2F7651",
          fontSize: "12px",
          fontWeight: 800,
          whiteSpace: "nowrap",
        }}
      >
        Invoice on WhatsApp
      </Typography>
    </Box>

    {onClose ? (
      <IconButton
        onClick={onClose}
        disabled={closeDisabled}
        aria-label="Close payment"
        sx={{
          backgroundColor: "#FFFFFF",
          boxShadow: "0 4px 16px rgba(14,73,97,0.12)",
          "&:hover": {
            backgroundColor: "#EFF7FB",
          },
          "&.Mui-disabled": {
            backgroundColor: "#FFFFFF",
            opacity: 0.65,
          },
        }}
      >
        <Close fontSize="small" />
      </IconButton>
    ) : null}
  </Box>
);

const PhoneDisplay = ({ phone, hasError }) => {
  const complete = phone.length === 10;

  return (
    <Box
      role="textbox"
      aria-label="Mobile number"
      aria-readonly="true"
      sx={{
        width: "100%",
        minHeight: "66px",
        display: "flex",
        alignItems: "center",
        borderRadius: "18px",
        backgroundColor: "#FFFFFF",
        border: hasError
          ? "2px solid #E05B5B"
          : complete
            ? "2px solid #16B36D"
            : "1.5px solid #A9D8EA",
        boxShadow: hasError
          ? "0 8px 20px rgba(224, 91, 91, 0.08)"
          : "0 5px 16px rgba(14, 112, 150, 0.07)",
        overflow: "hidden",
        transition: "all 0.2s ease",
      }}
    >
      <Box
        sx={{
          minWidth: 67,
          alignSelf: "stretch",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#EAF7FC",
          borderRight: "1px solid #D4EAF2",
        }}
      >
        <Typography
          sx={{
            color: "#0B4A67",
            fontSize: "17px",
            fontWeight: 800,
          }}
        >
          +91
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          px: 1.4,
          display: "flex",
          alignItems: "center",
          minHeight: 28,
        }}
      >
        {!phone && (
          <Typography
            sx={{
              color: "#A2B7C1",
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: "1px",
              whiteSpace: "nowrap",
            }}
          >
            Enter 10-digit number
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            letterSpacing: "1.5px",
          }}
        >
          <AnimatePresence initial={false}>
            {phone.split("").map((digit, index) => (
              <motion.span
                key={`${index}-${digit}`}
                initial={{ opacity: 0, y: 12, scale: 0.4 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.45 }}
                transition={{ type: "spring", stiffness: 640, damping: 22 }}
                style={{
                  display: "inline-block",
                  color: "#0B4A67",
                  fontSize: 18,
                  fontWeight: 800,
                  minWidth: "0.72em",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    color: "#0B4A67",
                    fontSize: "17px",
                    fontWeight: 800,
                  }}
                >{digit}</Typography>
              </motion.span>
            ))}
          </AnimatePresence>
        </Box>
      </Box>

      <AnimatePresence>
        {complete && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
          >
            <CheckCircleRounded
              sx={{
                color: "#16B36D",
                fontSize: 26,
                mr: 1.3,
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

const Keypad = ({
  phone,
  isLoading,
  handleNumberClick,
  handleBackspace,
  handleClear,
}) => (
  <Box
    sx={{
      p: 1.1,
      borderRadius: "22px",
      backgroundColor: "#DDEFF6",
      border: "1px solid #C9E5F0",
      boxShadow: "0 12px 28px rgba(18, 101, 137, 0.08)",
    }}
  >
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 0.9,
      }}
    >
      {keypadNumbers.map((number, index) => (
        <NumberButton
          key={number}
          number={number}
          index={index}
          disabled={isLoading || phone.length >= 10}
          onClick={() => handleNumberClick(number)}
        />
      ))}

      <ActionButton
        label="Clear"
        disabled={isLoading || !phone.length}
        onClick={handleClear}
      >
        Clear
      </ActionButton>

      <NumberButton
        number="0"
        index={9}
        disabled={isLoading || phone.length >= 10}
        onClick={() => handleNumberClick("0")}
      />

      <ActionButton
        label="Delete"
        disabled={isLoading || !phone.length}
        onClick={handleBackspace}
      >
        <BackspaceRounded />
      </ActionButton>
    </Box>
  </Box>
);

const NumberButton = ({ number, index, disabled, onClick }) => {
  const [pressBurst, setPressBurst] = useState(0);

  const handleClick = () => {
    if (disabled) return;
    setPressBurst((count) => count + 1);
    onClick();
  };

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        opacity: { delay: 0.06 + index * 0.025 },
        y: { delay: 0.06 + index * 0.025 },
      }}
      whileTap={
        disabled
          ? {}
          : {
            scale: 0.88,
            y: 3,
            backgroundColor: "#D7F1FA",
            boxShadow: "0 0 0 rgba(13, 130, 180, 0)",
            transition: { duration: 0.08, delay: 0 },
          }
      }
      disabled={disabled}
      onClick={handleClick}
      style={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        minHeight: "62px",
        border: "1px solid #BFDDE9",
        borderRadius: "16px",
        color: "#0B4A67",
        background: "#FFFFFF",
        boxShadow: "0 3px 0 rgba(13, 130, 180, 0.10)",
        fontSize: "23px",
        fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.48 : 1,
        WebkitTapHighlightColor: "transparent",
        touchAction: "manipulation",
      }}
    >
      <AnimatePresence>
        {pressBurst > 0 && (
          <motion.span
            key={pressBurst}
            initial={{ scale: 0, opacity: 0.5 }}
            animate={{ scale: 2.6, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.42, ease: "easeOut" }}
            style={{
              position: "absolute",
              inset: 0,
              margin: "auto",
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "rgba(9, 165, 247, 0.38)",
              pointerEvents: "none",
            }}
          />
        )}
      </AnimatePresence>

      <motion.span
        key={`digit-${pressBurst}`}
        initial={false}
        animate={pressBurst === 0 ? { scale: 1 } : { scale: [1, 1.22, 1] }}
        transition={{ duration: 0.22, ease: "easeOut" }}
        style={{ position: "relative", zIndex: 1, display: "inline-block" }}
      >
        {number}
      </motion.span>
    </motion.button>
  );
};

const ActionButton = ({ children, label, disabled, onClick }) => (
  <IconButton
    component={motion.button}
    type="button"
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    whileTap={disabled ? {} : { scale: 0.88, y: 2 }}
    sx={{
      width: "100%",
      minHeight: "58px",
      borderRadius: "16px",
      color: "#067EB3",
      backgroundColor: "#F2FBFE",
      border: "1px solid #BFDDE9",
      fontSize: "13px",
      fontWeight: 800,
      touchAction: "manipulation",
      WebkitTapHighlightColor: "transparent",
      "& svg": {
        fontSize: 24,
      },
      "&:hover": {
        backgroundColor: "#DDF1F9",
      },
      "&.Mui-disabled": {
        color: "#9EB8C4",
        backgroundColor: "#EFF8FC",
      },
    }}
  >
    {children}
  </IconButton>
);

export default PaymentProcessing;
